import { Dialog, DialogOverlay, DialogPortal, DialogTrigger } from '@/components/ui/dialog'
import * as React from 'react'
import { ReactNode, useEffect, useState } from 'react'
import * as DialogPrimitive from '@radix-ui/react-dialog'
import { cn } from '@/lib/utils'
import { Input } from '@/components/ui/input'
import { SearchIcon, TicketsPlaneIcon } from 'lucide-react'
import { Avatar, AvatarImage } from '@/components/ui/avatar'
import { Typography } from '@/components/ui/typography'
import { ellipseAddress } from '@/lib/display'
import Link from 'next/link'
import { useDebounce } from '@pancakeswap/hooks'
import { resolveHlName } from '@/utils/hl'
import { isAddress, zeroAddress } from 'viem'
import { useRecommendedPortfolios } from '@/hooks/use-recommended-portfolios'

export const SearchDialog = ({ trigger }: { trigger: ReactNode }) => {
  const [open, setOpen] = useState<boolean>(false)
  const [loading, setLoading] = useState(false)
  const [value, setValue] = useState('')
  const [result, setResult] = useState<{ name: string; address: string }>({
    name: '',
    address: '',
  })
  const debounceValue = useDebounce(value, 500)
  const { recommendedPortfolios, isLoading: isPortfolioLoading } = useRecommendedPortfolios()

  useEffect(() => {
    if (debounceValue) {
      const r = async () => {
        setResult({ name: '', address: '' })
        setLoading(true)
        try {
          const res = await resolveHlName(value)
          setResult({ name: isAddress(res.name) ? ellipseAddress(res.name) : res.name, address: res.address })
        } catch (e) {
          console.error(e)
        }
        setLoading(false)
      }
      r()
    }
  }, [debounceValue])

  return (
    <Dialog open={open} onOpenChange={setOpen}>
      <DialogTrigger asChild>{trigger}</DialogTrigger>
      <DialogPortal data-slot="dialog-portal">
        <DialogOverlay />
        <DialogPrimitive.Content
          data-slot="dialog-content"
          className={cn(
            'bg-background data-[state=open]:animate-in data-[state=closed]:animate-out data-[state=closed]:fade-out-0 data-[state=open]:fade-in-0 data-[state=closed]:zoom-out-95 data-[state=open]:zoom-in-95 fixed top-[50%] left-[50%] z-50 grid w-full max-w-[calc(100%-2rem)] translate-x-[-50%] translate-y-[-50%] gap-4 rounded-3xl border shadow-lg duration-200 sm:max-w-lg',
          )}
        >
          <div className="flex items-center px-4 py-1 border-b w-full overflow-hidden">
            <SearchIcon className={'size-4'} />
            <Input
              placeholder={'Search address or hl name'}
              className={'bg-transparent border-none py-2 text-sm'}
              value={value}
              onChange={(e) => setValue(e.target.value)}
            />
          </div>
          <div className="px-6 py-2">
            {result.address === zeroAddress && <Typography variant={'tip'}>No results found.</Typography>}
            {!!result.address && result.address !== zeroAddress && (
              <Link
                href={`/portfolio/${result.address}`}
                className="flex gap-2 hover:bg-foreground/5 px-2 py-1 rounded-lg -mx-2"
                key={result.address}
                onClick={() => setOpen(false)}
              >
                <Avatar className={'size-10 rounded-full'}>
                  <AvatarImage
                    src={`https://cdn.stamp.fyi/avatar/${result.address}`}
                    alt={'avatar'}
                    width={32}
                    height={32}
                  />
                </Avatar>
                <div>
                  <Typography>{result.name}</Typography>
                  <Typography variant={'tip'}>{ellipseAddress(result.address, 6)}</Typography>
                </div>
              </Link>
            )}
            {loading && (
              <div className="flex items-center gap-4">
                <div className={'size-12 bg-foreground/5 animate-pulse rounded-full grow shrink-0'} />
                <div className={'w-full flex flex-col gap-2'}>
                  <div className={'bg-foreground/5 animate-pulse h-5 rounded-sm w-1/2'} />
                  <div className={'bg-foreground/5 animate-pulse h-5 rounded-sm'} />
                </div>
              </div>
            )}
          </div>
          <div className={'px-6 pt-2 pb-6'}>
            <div className="flex items-center gap-2 text-foreground/60 text-sm mb-6">
              <TicketsPlaneIcon className={'size-4'} />
              Recommended profiles
            </div>
            <div className="flex flex-col gap-4">
              {isPortfolioLoading && (
                <div className="border rounded-xl p-4 flex gap-4">
                  <div className="bg-foreground/5 md:size-20 size-12 rounded-2xl" />
                  <div>
                    <div className="bg-foreground/5 w-16 h-5 rounded" />
                    <div className="bg-foreground/5 w-32 h-7 rounded my-2" />
                    <div className="bg-foreground/5 w-12 h-3 rounded" />
                  </div>
                </div>
              )}
              {recommendedPortfolios?.map((profile) => (
                <Link
                  href={`/portfolio/${profile.address}`}
                  className="flex gap-2 hover:bg-foreground/5 px-2 py-1 rounded-lg -mx-2"
                  key={profile.address}
                  onClick={() => setOpen(false)}
                >
                  <Avatar className={'size-10 rounded-full'}>
                    <AvatarImage
                      src={`https://cdn.stamp.fyi/avatar/${profile.address}`}
                      alt={'avatar'}
                      width={32}
                      height={32}
                    />
                  </Avatar>
                  <div>
                    <Typography>{profile.label}</Typography>
                    <Typography variant={'tip'}>{ellipseAddress(profile.address, 6)}</Typography>
                  </div>
                </Link>
              ))}
            </div>
          </div>
        </DialogPrimitive.Content>
      </DialogPortal>
    </Dialog>
  )
}
